/**
 * Copyright (c) 2025 Lauft App Inc. All rights reserved.
 *
 * VisionTest.js  —  TEMPORARY internal tool (admin dash).
 *
 * Search properties by address, then analyze ONE at a time on demand.
 * Shows, per room: the image the model used, the room + confidence, the
 * personality scores, and the NEW condition score (1-5 + raw). Display only —
 * nothing is written to the DB. Use it to sanity-check that condition scoring
 * works end-to-end (e.g. a kitchen you labeled 5 should come back ~4-5).
 *
 * Mount it in the admin dash like ConditionLabeler. Delete when done.
 */
import React, { useState, useCallback, useRef } from 'react';
import { Search, AlertCircle, Loader, Image as ImageIcon } from 'react-feather';
import AuthService from '../../services/authService';

const API_URL = 'https://back-end-x7tx.onrender.com/api';

// Palette (matches ConditionLabeler / agent portal light-mode)
const C = {
  bg: '#fcfcfc',
  panel: '#eeebe3',
  accent: '#fdd700',
  ink: '#1a1a1a',
  sage: '#7a9471',
  border: '#e2ddd2',
  muted: '#8a8578',
};

// Pretty room labels
const ROOM_LABEL = {
  kitchen: 'Kitchen',
  bathroom: 'Bathroom',
  living_room: 'Living / Main',
  front_exterior: 'Front Exterior',
  back_exterior: 'Back Exterior',
  dining_room: 'Dining',
  unknown: 'Unknown',
};

// LauftQ axes — low pole (score 0) vs high pole (score 100)
const LAUFTQ_AXES = [
  { key: 'dynamicVsCalm', low: 'Dynamic', high: 'Calm' },
  { key: 'modernVsTraditional', low: 'Modern', high: 'Traditional' },
  { key: 'socialVsPrivate', low: 'Social', high: 'Private' },
  { key: 'naturalVsEngineered', low: 'Natural', high: 'Engineered' },
];

// Condition 1-5 anchors (same as the labeler)
const COND_ANCHORS = ['Dated', 'Builder-grade', 'Updated', 'Renovated', 'Luxury'];

const VisionTest = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  const [analyzing, setAnalyzing] = useState(null); // propertyId currently analyzing
  const [analysis, setAnalysis] = useState(null);    // result for the selected property
  const [selected, setSelected] = useState(null);    // { id, address }

  const debounceRef = useRef(null);

  const runSearch = useCallback(async (q) => {
    if (q.trim().length < 3) {
      setResults([]);
      return;
    }
    setSearching(true);
    setError('');
    try {
      const res = await fetch(
        `${API_URL}/admin/labeling/vision-test/search?q=${encodeURIComponent(q.trim())}`,
        { headers: { Authorization: `Bearer ${AuthService.getToken()}` } }
      );
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Search failed');
      setResults(json.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSearching(false);
    }
  }, []);

  const onQueryChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 350);
  };

  const analyze = async (prop) => {
    setAnalyzing(prop.id);
    setSelected({ id: prop.id, address: prop.address });
    setAnalysis(null);
    setError('');
    try {
      const res = await fetch(`${API_URL}/admin/labeling/vision-test/analyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${AuthService.getToken()}`,
        },
        body: JSON.stringify({ propertyId: prop.id }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Analysis failed');
      setAnalysis(json.data);
    } catch (e) {
      setError(e.message);
    } finally {
      setAnalyzing(null);
    }
  };

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div>
          <h2 style={{ margin: 0, color: C.ink }}>Vision Test</h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>
            Search an address, analyze one property at a time. Shows personality +
            condition per room. Nothing is saved.
          </div>
        </div>
      </div>

      {/* Search */}
      <div style={S.searchBar}>
        <Search size={18} color={C.muted} />
        <input
          style={S.searchInput}
          placeholder="Search by address (min 3 chars)…"
          value={query}
          onChange={onQueryChange}
        />
        {searching && <Loader size={16} color={C.muted} className="spin" />}
      </div>

      {error && (
        <div style={S.errorBar}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {/* Results list */}
      {results.length > 0 && (
        <div style={S.resultList}>
          {results.map((p) => (
            <div key={p.id} style={S.resultRow}>
              <div style={S.resultThumb}>
                {p.thumb ? (
                  <img src={p.thumb} alt="" style={S.thumbImg} />
                ) : (
                  <ImageIcon size={18} color={C.muted} />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={S.resultAddr}>{p.address}</div>
                <div style={S.resultMeta}>
                  {p.price}
                  {p.status ? ` · ${p.status}` : ''}
                </div>
              </div>
              <button
                style={analyzing === p.id ? S.btnBusy : S.btn}
                onClick={() => analyze(p)}
                disabled={!!analyzing}
              >
                {analyzing === p.id ? 'Analyzing…' : 'Analyze'}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Analysis result */}
      {selected && (
        <div style={S.analysisPanel}>
          <div style={S.analysisHead}>
            <strong style={{ color: C.ink }}>{selected.address}</strong>
            {analysis && (
              <span style={{ color: C.muted, fontSize: 13 }}>
                {' '}· {analysis.roomsFound} rooms found · {analysis.totalImages} photos
                {analysis.imagesFailed ? ` · ${analysis.imagesFailed} failed` : ''}
              </span>
            )}
          </div>

          {analyzing === selected.id && (
            <div style={S.center}>
              <Loader size={28} color={C.sage} className="spin" />
              <p style={{ marginTop: 10, color: C.muted }}>
                Running through Vision (downloads each image, scores it)…
              </p>
            </div>
          )}

          {analysis && analysis.rooms.length === 0 && (
            <div style={S.center}>
              <p style={{ color: C.muted }}>
                No rooms met the 80% confidence threshold.
              </p>
            </div>
          )}

          {analysis &&
            analysis.rooms.map((room) => {
              const cond = room.conditionScore;
              return (
                <div key={room.roomType} style={S.roomCard}>
                  <div style={S.roomImgWrap}>
                    <img src={room.imageUrl} alt={room.roomType} style={S.roomImg} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={S.roomTitle}>
                      {ROOM_LABEL[room.roomType] || room.roomType}
                      <span style={{ color: C.muted, fontWeight: 400, fontSize: 12 }}>
                        {' '}· {(room.roomConfidence * 100).toFixed(0)}% conf
                      </span>
                    </div>

                    {/* Condition */}
                    <div style={S.condRow}>
                      {cond ? (
                        <>
                          <span style={S.condBadge}>
                            Condition {cond.score} · {COND_ANCHORS[cond.score - 1]}
                          </span>
                          <span style={{ color: C.muted, fontSize: 12 }}>
                            raw {cond.rawScore} · {cond.field}
                          </span>
                        </>
                      ) : (
                        <span style={{ color: C.muted, fontSize: 13 }}>
                          No condition head for this room
                        </span>
                      )}
                    </div>

                    {/* Personality */}
                    <div style={S.persGrid}>
                      {LAUFTQ_AXES.map((ax) => {
                        const v = room.personalityScores?.[ax.key];
                        return (
                          <div key={ax.key} style={S.persItem}>
                            <div style={S.persLabels}>
                              <span>{ax.low}</span>
                              <span>{ax.high}</span>
                            </div>
                            <div style={S.persTrack}>
                              <div
                                style={{
                                  ...S.persFill,
                                  left: `${v == null ? 50 : v}%`,
                                }}
                              />
                            </div>
                            <div style={S.persVal}>{v == null ? '—' : v}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      <style>{`.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

const S = {
  wrap: { padding: 24, background: C.bg, minHeight: '100%', fontFamily: 'system-ui, sans-serif' },
  header: { marginBottom: 16 },
  center: { textAlign: 'center', padding: 40, color: C.ink },
  searchBar: {
    display: 'flex', alignItems: 'center', gap: 10, background: '#fff',
    border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 12,
  },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: 15, background: 'transparent', color: C.ink },
  errorBar: {
    display: 'flex', alignItems: 'center', gap: 8, background: '#fdecea',
    color: '#b71c1c', border: '1px solid #f5c6cb', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 14,
  },
  resultList: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 },
  resultRow: {
    display: 'flex', alignItems: 'center', gap: 12, background: '#fff',
    border: `1px solid ${C.border}`, borderRadius: 10, padding: 10,
  },
  resultThumb: {
    width: 48, height: 48, borderRadius: 8, background: C.panel, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  resultAddr: { color: C.ink, fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  resultMeta: { color: C.muted, fontSize: 12, marginTop: 2 },
  btn: {
    background: C.accent, color: C.ink, border: 'none', borderRadius: 8,
    padding: '8px 16px', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  },
  btnBusy: {
    background: C.panel, color: C.muted, border: 'none', borderRadius: 8,
    padding: '8px 16px', fontWeight: 600, cursor: 'wait', flexShrink: 0,
  },
  analysisPanel: {
    background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 16, marginTop: 8,
  },
  analysisHead: { marginBottom: 12, paddingBottom: 10, borderBottom: `1px solid ${C.border}` },
  roomCard: {
    display: 'flex', gap: 14, padding: 12, borderRadius: 10,
    background: C.bg, border: `1px solid ${C.border}`, marginBottom: 10,
  },
  roomImgWrap: { width: 140, height: 105, borderRadius: 8, overflow: 'hidden', flexShrink: 0, background: C.panel },
  roomImg: { width: '100%', height: '100%', objectFit: 'cover' },
  roomTitle: { color: C.ink, fontWeight: 700, fontSize: 15, marginBottom: 8 },
  condRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
  condBadge: {
    background: C.sage, color: '#fff', borderRadius: 6, padding: '4px 10px', fontWeight: 600, fontSize: 13,
  },
  persGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  persItem: {},
  persLabels: { display: 'flex', justifyContent: 'space-between', fontSize: 11, color: C.muted, marginBottom: 3 },
  persTrack: { position: 'relative', height: 6, background: C.panel, borderRadius: 3 },
  persFill: {
    position: 'absolute', top: -3, width: 12, height: 12, borderRadius: '50%',
    background: C.ink, transform: 'translateX(-50%)',
  },
  persVal: { fontSize: 11, color: C.muted, textAlign: 'center', marginTop: 4 },
};

export default VisionTest;